<script setup>
import { ref, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const baseUrl     = import.meta.env.VITE_CHATWOOT_BASE_URL
const websiteToken = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN
const enabled     = Boolean(baseUrl && websiteToken)

const loaded = ref(false)
const loading = ref(false)
const isOpen = ref(false)
let cleanupListener = null
let cleanupToggle = null

// With hideMessageBubble the panel carries its own close (X) in the header
// and the SDK announces every open/close, so our launcher simply steps
// aside while the panel is up and returns when it closes.
function bindToggleEvents() {
  const onOpen  = () => { isOpen.value = true }
  const onClose = () => { isOpen.value = false }
  window.addEventListener("chatwoot:opened", onOpen)
  window.addEventListener("chatwoot:closed", onClose)
  cleanupToggle = () => {
    window.removeEventListener("chatwoot:opened", onOpen)
    window.removeEventListener("chatwoot:closed", onClose)
  }
}

function injectScript() {
  return new Promise((resolve, reject) => {
    if (window.chatwootSDK) return resolve()
    const s = document.createElement('script')
    s.src = `${baseUrl}/packs/js/sdk.js`
    s.defer = true
    s.async = true
    s.onload  = () => resolve()
    s.onerror = () => reject(new Error('Chatwoot SDK failed to load'))
    document.body.appendChild(s)
  })
}

function whenReady(fn) {
  if (window.$chatwoot) return fn()
  const handler = () => { window.removeEventListener('chatwoot:ready', handler); fn() }
  window.addEventListener('chatwoot:ready', handler)
  cleanupListener = () => window.removeEventListener('chatwoot:ready', handler)
}

async function open() {
  if (!enabled || loading.value) return
  if (loaded.value) { window.$chatwoot?.toggle('open'); return }
  loading.value = true
  try {
    // Our launcher is the only bubble on the page: Chatwoot's own launcher
    // would otherwise appear next to ours the moment the SDK boots.
    window.chatwootSettings = { hideMessageBubble: true, position: 'right', type: 'standard', darkMode: 'auto' }
    await injectScript()
    window.chatwootSDK.run({ websiteToken, baseUrl })
    whenReady(() => {
      bindToggleEvents()
      window.$chatwoot.toggle('open')
      loaded.value = true
      loading.value = false
      isOpen.value = true
    })
  } catch (e) {
    loading.value = false
    console.warn('[ChatwootWidget]', e)
  }
}

onUnmounted(() => { cleanupListener?.(); cleanupToggle?.() })
</script>

<template>
  <button v-if="enabled" v-show="!isOpen" type="button" class="cw-bubble" :disabled="loading" :aria-expanded="isOpen" @click="open">
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
    </svg>
    <span class="cw-bubble__label">{{ t('public.widget.cta') }}</span>
  </button>
</template>

<style scoped>
/* Same voice as LuxButton (dark tone, solid): foam pill, gold hairline,
   uppercase spaced label — the launcher is a concierge bell, not an app FAB. */
.cw-bubble {
  position: fixed; right: 1.25rem; bottom: max(env(safe-area-inset-bottom), 1.25rem);
  display: inline-flex; align-items: center; gap: 0.6rem;
  padding: 0.85rem 1.3rem 0.85rem 1.1rem;
  background: var(--night, #0B2537); color: var(--foam, #ECF3F6);
  border: 1px solid color-mix(in oklch, var(--gold, #D8B46B) 65%, transparent); border-radius: 999px;
  font-family: var(--font-body, var(--body)); font-weight: 600; font-size: 0.74rem;
  letter-spacing: 0.14em; text-transform: uppercase; line-height: 1;
  cursor: pointer; z-index: 60;
  box-shadow: 0 18px 40px -20px rgba(0, 0, 0, 0.7), inset 0 0 0 1px color-mix(in oklch, var(--gold, #D8B46B) 25%, transparent);
  transition: transform 150ms ease, border-color 150ms ease, background 150ms ease;
}
.cw-bubble:hover    { transform: translateY(-1px); border-color: var(--gold, #D8B46B); background: var(--night2, #123047); }
.cw-bubble:disabled { opacity: 0.7; cursor: progress; }
@media (max-width: 480px) { .cw-bubble__label { display: none; } .cw-bubble { padding: 0.7rem; } }
</style>
